(() => {
  const SELECTORS = {
    articleRoot: '[data-db-article]',
    contentRoot: '[data-article-content]',
    mount: '[data-db-toc-mount]',
  };

  const WARNING_ICON_BASE = 'https://cdn.shopify.com/s/files/1/0728/3264/7364/files/dogwarning.webp?v=1767725794';

/**
 * Shopify CDN supports on-the-fly resizing via a `width=` query param.
 * Use this to avoid downloading the full 500×500 asset for a small UI icon.
 */
const withWidth = (url, width) => {
  const joiner = url.includes('?') ? '&' : '?';
  return `${url}${joiner}width=${encodeURIComponent(width)}`;
};
  const WARNING_TEXT =
    'This article is provided for general educational and informational purposes only and is not a substitute for professional veterinary advice, diagnosis, or treatment—always seek the guidance of a licensed veterinarian with any questions regarding your pet’s health.';

  const prefersReducedMotion = () => {
    if (!('matchMedia' in window)) return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  };

  const isPlainLeftClick = (e) => {
    // Don't hijack modified clicks (new tab, etc.)
    if (!e) return false;
    if ('button' in e && e.button !== 0) return false;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return false;
    return true;
  };

  const smoothScrollToEl = (targetEl) => {
    if (!targetEl) return;
    const behavior = prefersReducedMotion() ? 'auto' : 'smooth';
    try {
      targetEl.scrollIntoView({ behavior, block: 'start' });
    } catch (err) {
      // Older browsers: no options support
      targetEl.scrollIntoView();
    }
  };

  const updateHash = (id) => {
    if (!id) return;
    const next = `#${encodeURIComponent(id)}`;
    if (window.location.hash === next) return;
    try {
      if (history && 'pushState' in history) history.pushState(null, '', next);
      else window.location.hash = next;
    } catch (err) {
      window.location.hash = next;
    }
  };

  const slugify = (text) => {
    const s = String(text || '')
      .trim()
      .toLowerCase()
      .replace(/&amp;|&/g, ' and ')
      .replace(/[’']/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 64);
    return s || 'section';
  };

  const ensureId = (el, used) => {
    if (!el) return '';
    let id = (el.getAttribute('id') || '').trim();
    if (!id) {
      const base = slugify(el.textContent || '');
      id = base;
      let i = 2;
      while (used.has(id) || document.getElementById(id)) {
        id = `${base}-${i++}`;
      }
      el.setAttribute('id', id);
    } else {
      // Ensure uniqueness if an existing ID clashes.
      if (used.has(id)) {
        const base = slugify(id);
        let next = base;
        let i = 2;
        while (used.has(next) || document.getElementById(next)) {
          next = `${base}-${i++}`;
        }
        id = next;
        el.setAttribute('id', id);
      }
    }
    used.add(id);
    return id;
  };

  const getHeadings = (contentRoot) => {
    const used = new Set();
    const headings = Array.from(contentRoot.querySelectorAll('h2'))
      .filter((h) => {
        if (!h) return false;
        if (h.closest('.db-toc')) return false;
        if (h.closest('.db-toc-float')) return false;
        const txt = (h.textContent || '').trim();
        return txt.length > 0;
      })
      .map((h) => {
        const id = ensureId(h, used);
        const label = (h.textContent || '').trim();
        return { el: h, id, label };
      })
      .filter((x) => x.id);

    return headings;
  };

  const el = (tag, attrs = {}, children = []) => {
    const node = document.createElement(tag);
    for (const [k, v] of Object.entries(attrs || {})) {
      if (v === null || v === undefined || v === false) continue;
      if (k === 'class') node.className = String(v);
      else if (k === 'text') node.textContent = String(v);
      else if (k === 'html') node.innerHTML = String(v);
      else if (k.startsWith('data-')) node.setAttribute(k, String(v));
      else if (k === 'aria') {
        for (const [ak, av] of Object.entries(v || {})) {
          if (av === null || av === undefined || av === false) continue;
          node.setAttribute(`aria-${ak}`, String(av));
        }
      } else node.setAttribute(k, String(v));
    }
    for (const c of children) {
      if (c === null || c === undefined) continue;
      if (typeof c === 'string') node.appendChild(document.createTextNode(c));
      else node.appendChild(c);
    }
    return node;
  };

  const buildQuickLinks = ({ topId, allUrl, includeTop = true }) => {
    const grid = el('div', { class: 'db-toc__quickGrid' });

    if (includeTop) {
      const topLink = el(
        'a',
        {
          class: 'db-toc__quickLink',
          href: `#${encodeURIComponent(topId)}`,
        },
        [
          el('span', { text: 'Back to top' }),
          el('span', { class: 'db-toc__quickLinkIcon', 'aria-hidden': 'true', text: '↑' }),
        ]
      );

      grid.appendChild(topLink);
    }

    const allLink = el(
      'a',
      {
        class: 'db-toc__quickLink',
        href: allUrl,
      },
      [
        el('span', { text: 'Back to All dog breed articles' }),
        el('span', { class: 'db-toc__quickLinkIcon', 'aria-hidden': 'true', text: '↩' }),
      ]
    );

    grid.appendChild(allLink);

    const wrap = el('div', { class: 'db-toc__quick' }, [
      el('p', { class: 'db-toc__quickLabel', text: 'Quick links' }),
      grid,
    ]);

    return wrap;
  };

  const buildSectionList = (headings) => {
    const list = el('ol', { class: 'db-toc__list' });

    for (const h of headings) {
      const a = el('a', {
        class: 'db-toc__link',
        href: `#${encodeURIComponent(h.id)}`,
        'data-db-toc-link': h.id,
      });
      a.textContent = h.label;

      const li = el('li', { class: 'db-toc__item' }, [a]);
      list.appendChild(li);
    }

    return list;
  };

  const buildInlineTOC = ({ headings, topId, allUrl }) => {
    const root = el('aside', { class: 'db-toc', 'data-db-toc-inline': '', aria: { label: 'On this page' } });

    const badge = el('div', { class: 'db-toc__badge', role: 'note' }, [
      el('img', {
        src: withWidth(WARNING_ICON_BASE, 60),
        srcset: `${withWidth(WARNING_ICON_BASE, 30)} 30w, ${withWidth(WARNING_ICON_BASE, 60)} 60w`,
        sizes: '30px',
        width: '30',
        height: '30',
        alt: 'Dog health warning icon',
        loading: 'lazy',
        decoding: 'async',
        fetchpriority: 'low',
      }),
      el('p', { text: WARNING_TEXT }),
    ]);

    const inner = el('div', { class: 'db-toc__inner' });

    const title = el('div', { class: 'db-toc__title' }, [
      el('strong', { text: 'On this page' }),
      el('span', { text: String(headings.length) }),
    ]);

    const nav = el('nav', { aria: { label: 'Table of contents' } });
    nav.appendChild(buildSectionList(headings));

    inner.appendChild(title);
    // Inline menu: no "Back to top" (per latest request)
    inner.appendChild(buildQuickLinks({ topId, allUrl, includeTop: false }));
    inner.appendChild(nav);

    root.appendChild(badge);
    root.appendChild(inner);

    return root;
  };

  const buildFloatingTOC = ({ headings, topId, allUrl }) => {
    const wrap = el('div', { class: 'db-toc-float', 'data-db-toc-float': '' });

    const panelId = 'db-toc-panel';

    const button = el(
      'button',
      {
        class: 'db-toc-fab',
        type: 'button',
        aria: { expanded: 'false', controls: panelId, label: 'Open on-page menu' },
      },
      [
        el('span', { class: 'db-toc-fab__icon', 'aria-hidden': 'true', text: '≡' }),
        el('span', { class: 'db-toc-fab__label', text: 'Menu' }),
      ]
    );

    const panel = el('div', { class: 'db-toc-panel', id: panelId });
    const header = el('div', { class: 'db-toc-panel__header' }, [
      el('strong', { text: 'On this page' }),
      el(
        'button',
        {
          class: 'db-toc-panel__close',
          type: 'button',
          aria: { label: 'Close menu' },
        },
        [el('span', { 'aria-hidden': 'true', text: '×' })]
      ),
    ]);

    const body = el('div', { class: 'db-toc-panel__body' });
    // Floating menu: includes "Back to top" + "All dog breed articles"
    body.appendChild(buildQuickLinks({ topId, allUrl, includeTop: true }));

    const nav = el('nav', { aria: { label: 'Table of contents' } });
    nav.appendChild(buildSectionList(headings));
    body.appendChild(nav);

    panel.appendChild(header);
    panel.appendChild(body);

    wrap.appendChild(panel);
    wrap.appendChild(button);

    return { wrap, button, panel };
  };

  const setActiveId = (id) => {
    const links = Array.from(document.querySelectorAll('[data-db-toc-link]'));
    for (const a of links) {
      const match = a.getAttribute('data-db-toc-link') === id;
      a.classList.toggle('is-active', match);
      if (match) a.setAttribute('aria-current', 'location');
      else a.removeAttribute('aria-current');
    }
  };

  const setupSmoothScrollLinks = ({ containerEl, headingIdSet, onNavigate }) => {
    if (!containerEl) return;

    containerEl.addEventListener('click', (e) => {
      const t = e.target;
      if (!(t instanceof Element)) return;

      const a = t.closest('a[href]');
      if (!a) return;

      const href = (a.getAttribute('href') || '').trim();
      if (!href || !href.startsWith('#')) return;
      if (!isPlainLeftClick(e)) return;

      const raw = href.replace(/^#/, '');
      if (!raw) return;

      const id = decodeURIComponent(raw);
      const target = document.getElementById(id);
      if (!target) return;

      e.preventDefault();

      if (typeof onNavigate === 'function') onNavigate();

      // Update URL without the browser's default jump (we scroll manually).
      updateHash(id);

      // Update active styling immediately (IntersectionObserver will keep it in sync).
      if (headingIdSet && headingIdSet.has(id)) setActiveId(id);

      smoothScrollToEl(target);
    });
  };

  const setupActiveObserver = (headings) => {
    const validIds = new Set(headings.map((h) => h.id));

    const setFromHash = () => {
      const raw = (window.location.hash || '').replace(/^#/, '');
      if (!raw) return;
      const id = decodeURIComponent(raw);
      if (validIds.has(id)) setActiveId(id);
    };

    setFromHash();

    window.addEventListener('hashchange', setFromHash);

    if (!('IntersectionObserver' in window)) return;

    let current = '';
    const io = new IntersectionObserver(
      (entries) => {
        // Pick the entry closest to the top that is intersecting.
        const visibles = entries
          .filter((e) => e.isIntersecting && e.target && e.target.id)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);

        if (!visibles.length) return;

        const next = visibles[0].target.id;
        if (next && next !== current) {
          current = next;
          setActiveId(current);
        }
      },
      {
        root: null,
        // Activate headings as they approach the top third of the viewport.
        rootMargin: '-20% 0px -70% 0px',
        threshold: 0,
      }
    );

    for (const h of headings) {
      if (h && h.el) io.observe(h.el);
    }
  };

  const setupFloatingBehaviour = ({ inlineEl, floatWrap, floatButton, floatPanel, headingIdSet }) => {
    if (!inlineEl || !floatWrap || !floatButton || !floatPanel) return;

    const close = () => {
      floatWrap.classList.remove('is-open');
      floatButton.setAttribute('aria-expanded', 'false');
    };

    const open = () => {
      floatWrap.classList.add('is-open');
      floatButton.setAttribute('aria-expanded', 'true');

      // Focus the panel header for screen readers (but avoid forcing focus if user prefers reduced motion)
      if (!prefersReducedMotion()) {
        const focusTarget = floatPanel.querySelector('.db-toc-panel__header');
        if (focusTarget && focusTarget instanceof HTMLElement) {
          focusTarget.setAttribute('tabindex', '-1');
          focusTarget.focus({ preventScroll: true });
        }
      }
    };

    const toggle = () => {
      if (floatWrap.classList.contains('is-open')) close();
      else open();
    };

    // Toggle open/close
    floatButton.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      toggle();
    });

    const closeBtn = floatPanel.querySelector('.db-toc-panel__close');
    if (closeBtn) {
      closeBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        close();
      });
    }

    // Close when clicking outside
    document.addEventListener('click', (e) => {
      if (!floatWrap.classList.contains('is-open')) return;
      const t = e.target;
      if (!(t instanceof Node)) return;
      if (!floatWrap.contains(t)) close();
    });

    // Close with Escape
    document.addEventListener('keydown', (e) => {
      if (!floatWrap.classList.contains('is-open')) return;
      if (e.key === 'Escape') close();
    });

    // Smooth-scroll for all on-page links inside the floating panel.
    // (Also closes the panel after navigating.)
    setupSmoothScrollLinks({
      containerEl: floatPanel,
      headingIdSet,
      onNavigate: close,
    });

    // Reveal/hide button when the inline menu scrolls out of view.
    // Prefer IntersectionObserver so we don't run layout checks on every scroll event.
    const setFloatingVisible = (show) => {
      floatWrap.classList.toggle('is-visible', show);
      if (!show) close();
    };

    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        const entry = entries && entries[0];
        if (!entry) return;
        const show = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
        setFloatingVisible(show);
      });
      io.observe(inlineEl);
    } else {
      let ticking = false;
      const updateVisibility = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          ticking = false;
          const rect = inlineEl.getBoundingClientRect();
          setFloatingVisible(rect.bottom < 0);
        });
      };

      window.addEventListener('scroll', updateVisibility, { passive: true });
      window.addEventListener('resize', updateVisibility);
      updateVisibility();
    }
  };

  const ensureTopAnchor = (articleRoot, topId) => {
    if (!articleRoot) return;
    if (document.getElementById(topId)) return;

    const anchor = document.createElement('div');
    anchor.id = topId;
    anchor.className = 'db-breed-top';
    anchor.setAttribute('aria-hidden', 'true');

    // Put it at the very top of the article.
    articleRoot.insertAdjacentElement('afterbegin', anchor);
  };

  const run = () => {
    const articleRoot = document.querySelector(SELECTORS.articleRoot);
    if (!articleRoot) return;

    const contentRoot = articleRoot.querySelector(SELECTORS.contentRoot) || articleRoot;
    const mount = contentRoot.querySelector(SELECTORS.mount);
    if (!mount) return;

    // Top anchor + blog URL
    const topId = 'db-breed-top';
    ensureTopAnchor(articleRoot, topId);

    let allUrl =
      (articleRoot.getAttribute('data-db-all-url') || '').trim() ||
      (articleRoot.dataset && articleRoot.dataset.dbAllUrl ? String(articleRoot.dataset.dbAllUrl).trim() : '') ||
      '/blogs/dog-breeds';

    // If the user navigated here from the Dog Breeds hub, prefer that exact URL
    // (includes filters, per-page, and current pagination state).
    try {
      const storedHubUrl = window.sessionStorage ? window.sessionStorage.getItem('DB_DOG_BREEDS_HUB_URL') : '';
      if (storedHubUrl && typeof storedHubUrl === 'string') {
        allUrl = storedHubUrl;
      }
    } catch (e) {
      // Ignore sessionStorage failures
    }

    const headings = getHeadings(contentRoot);
    if (!headings.length) {
      // No H2s? Don’t render the menu.
      mount.innerHTML = '';
      return;
    }

    const headingIdSet = new Set(headings.map((h) => h.id));

    // Build + mount inline menu
    mount.innerHTML = '';
    const inlineTOC = buildInlineTOC({ headings, topId, allUrl });
    mount.appendChild(inlineTOC);

    // Smooth-scroll for the inline menu links
    setupSmoothScrollLinks({ containerEl: inlineTOC, headingIdSet });

    // Build floating menu and append to the end of the article (so it inherits .db-breed scope)
    const { wrap, button, panel } = buildFloatingTOC({ headings, topId, allUrl });
    articleRoot.appendChild(wrap);

    setupActiveObserver(headings);
    setupFloatingBehaviour({
      inlineEl: inlineTOC,
      floatWrap: wrap,
      floatButton: button,
      floatPanel: panel,
      headingIdSet,
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
})();
